import type {
  ClaimResponse,
  MarketingSavingsResponse,
  RewardDetailResponse,
  RewardsListResponse,
  SavingsSummaryResponse,
  UserAddressResponse,
  Reward,
} from "./types";
import { MOCK_USER_SUBSCRIPTION } from "./MOCK_DATA";

const MOCK_DELAY_MS = 400;

function delay(ms = MOCK_DELAY_MS): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Reward items — aligned with rewards_offers + rewards_offer_tiers spec
// ---------------------------------------------------------------------------

const welcomeEnvelope: Reward = {
  Id: "rw-welcome-envelope",
  RewardType: "PHYSICAL",
  IsInternal: true,
  Status: "claimable",
  DisplayName: "Welcome Envelope",
  LockedDisplayName: null,
  Description: "A welcome package, delivered to you.",
  PartnerName: "BOTB",
  PartnerSlug: "botb",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-welcome.svg",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-welcome.png",
  MonetaryValue: 0,
  Currency: "GBP",
  ValueDescription: "Physical booklet",
  Quantity: null,
  RequiresAddress: true,
  AllowedCountries: ["GB", "IE"],
  DisplayBeforeUnlock: true,
  UnlockDate: null,
  DaysUntilUnlock: null,
  TimelineOrder: 1,
  TimelineIconType: "gift",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

const cashbackReward: Reward = {
  Id: "rw-cashback",
  RewardType: "CREDIT",
  IsInternal: true,
  Status: "claimable",
  DisplayName: "Cashback",
  LockedDisplayName: null,
  Description: "Earn money back on every direct competition entry.",
  PartnerName: "BOTB",
  PartnerSlug: "botb",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-cashback.svg",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-cashback.png",
  MonetaryValue: null,
  Currency: "GBP",
  ValueDescription: "2%",
  Quantity: null,
  RequiresAddress: false,
  AllowedCountries: [],
  DisplayBeforeUnlock: true,
  UnlockDate: null,
  DaysUntilUnlock: null,
  TimelineOrder: 2,
  TimelineIconType: "cashback",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

const mysteryPrize: Reward = {
  Id: "rw-mystery-prize",
  RewardType: "TICKETS",
  IsInternal: true,
  Status: "locked",
  DisplayName: "Mystery Prize",
  LockedDisplayName: "Mystery Prize",
  Description: "Dream Home competition tickets, revealed when unlocked.",
  PartnerName: "BOTB",
  PartnerSlug: "botb",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-mystery.svg",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-mystery.png",
  MonetaryValue: 10,
  Currency: "GBP",
  ValueDescription: "10 Dream Home tickets",
  Quantity: 10,
  RequiresAddress: false,
  AllowedCountries: [],
  DisplayBeforeUnlock: true,
  UnlockDate: "2026-04-04T00:00:00Z",
  DaysUntilUnlock: 23,
  TimelineOrder: 3,
  TimelineIconType: "lock",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

const botbMerch: Reward = {
  Id: "rw-botb-merch",
  RewardType: "PHYSICAL",
  IsInternal: true,
  Status: "locked",
  DisplayName: "BOTB Merch Drop",
  LockedDisplayName: "BOTB Merch Drop",
  Description: "Exclusive BOTB gear, chosen for you.",
  PartnerName: "BOTB",
  PartnerSlug: "botb",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-merch.svg",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-merch.png",
  MonetaryValue: 0,
  Currency: "GBP",
  ValueDescription: "~£20-25 bundle",
  Quantity: null,
  RequiresAddress: true,
  AllowedCountries: ["GB", "IE"],
  DisplayBeforeUnlock: true,
  UnlockDate: "2026-05-04T00:00:00Z",
  DaysUntilUnlock: 53,
  TimelineOrder: 4,
  TimelineIconType: "lock",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

const nortonPerk: Reward = {
  Id: "pk-norton",
  RewardType: "VOUCHER_CODE",
  IsInternal: false,
  Status: "claimable",
  DisplayName: "Norton 360 Deluxe",
  LockedDisplayName: "Norton 360 Deluxe",
  Description: "12-month subscription to Norton 360 Deluxe — VPN, antivirus, and dark web monitoring.",
  PartnerName: "Norton",
  PartnerSlug: "norton",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-norton.png",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-norton.jpg",
  MonetaryValue: 39.99,
  Currency: "GBP",
  ValueDescription: "12 months free",
  Quantity: null,
  RequiresAddress: false,
  AllowedCountries: [],
  DisplayBeforeUnlock: true,
  UnlockDate: null,
  DaysUntilUnlock: null,
  TimelineOrder: 5,
  TimelineIconType: "star",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

const expressVpnPerk: Reward = {
  Id: "pk-express-vpn",
  RewardType: "VOUCHER_CODE",
  IsInternal: false,
  Status: "locked",
  DisplayName: "ExpressVPN 6-Month",
  LockedDisplayName: "ExpressVPN",
  Description: "6 months of ExpressVPN — ultra-fast, secure browsing on all your devices.",
  PartnerName: "ExpressVPN",
  PartnerSlug: "expressvpn",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-express-vpn.svg",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-express-vpn.jpg",
  MonetaryValue: 49.99,
  Currency: "GBP",
  ValueDescription: "3 months free",
  Quantity: null,
  RequiresAddress: false,
  AllowedCountries: [],
  DisplayBeforeUnlock: true,
  UnlockDate: "2026-04-04T00:00:00Z",
  DaysUntilUnlock: 23,
  TimelineOrder: 6,
  TimelineIconType: "lock",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

const aloSimPerk: Reward = {
  Id: "pk-alosim",
  RewardType: "VOUCHER_CODE",
  IsInternal: false,
  Status: "locked",
  DisplayName: "aloSIM Travel eSIM",
  LockedDisplayName: "aloSIM",
  Description: "Stay connected abroad with a free aloSIM travel eSIM data package.",
  PartnerName: "aloSIM",
  PartnerSlug: "alosim",
  PartnerLogoUrl: "",
  IconUrl: "/assets/images/botb-rewards/icon-alo-sim.svg",
  BackgroundImageUrl: "/assets/images/botb-rewards/card-bg-alo-sim.jpg",
  MonetaryValue: 10,
  Currency: "GBP",
  ValueDescription: "10GB data",
  Quantity: null,
  RequiresAddress: false,
  AllowedCountries: [],
  DisplayBeforeUnlock: true,
  UnlockDate: "2026-04-04T00:00:00Z",
  DaysUntilUnlock: 23,
  TimelineOrder: 7,
  TimelineIconType: "lock",
  ClaimId: null,
  ClaimType: null,
  FulfillmentStatus: null,
  VoucherCode: null,
  VoucherExpiresAt: null,
};

// ---------------------------------------------------------------------------
// Mock response builders
// ---------------------------------------------------------------------------

export const MOCK_REWARDS_LIST: RewardsListResponse = {
  MemberRewards: [welcomeEnvelope, cashbackReward, mysteryPrize, botbMerch],
  MemberPerks: [nortonPerk, expressVpnPerk, aloSimPerk],
  PerksUnlocked: true,
  PerksUnlockDate: null,
  PerksTotalValue: 99.98,
};

export const MOCK_SAVINGS_SUMMARY: SavingsSummaryResponse = {
  LifetimeTotal: 487.65,
  LifetimePassSavings: 326.5,
  LifetimeCashback: 81.15,
  LifetimeRewardsPerks: 80.0,
  CurrentPeriodTotal: 45.3,
  CurrentPeriodPass: 45.3,
  CurrentPeriodCashback: 0,
  CurrentPeriodRewardsPerks: 0,
  CurrentPeriodStart: "14 Feb 26",
  CurrentPeriodEnd: "13 Mar 26",
  CurrentTier: "PREMIUM",
  CurrentPlanType: "MONTHLY",
  IsCashbackActive: false,
  TotalCompetitionsParticipated: 12,
  FirstName: "Sarah",
  Surname: "Johnson",
  MemberSince: 1780099200, // 30 May 2026
};

export const MOCK_MARKETING_SAVINGS: MarketingSavingsResponse = {
  Headline: "Members save up to £87/month",
  Tiers: [
    { TierName: "Essential", AvgMonthlySavings: 18, AvgAnnualSavings: 216 },
    { TierName: "Premium", AvgMonthlySavings: 42, AvgAnnualSavings: 504 },
    { TierName: "Ultimate", AvgMonthlySavings: 87, AvgAnnualSavings: 1044 },
  ],
};

export const MOCK_REWARDS_LIST_ALL_LOCKED: RewardsListResponse = {
  MemberRewards: [welcomeEnvelope, cashbackReward, mysteryPrize, botbMerch],
  MemberPerks: [
    { ...nortonPerk, Status: "locked", DaysUntilUnlock: 23, UnlockDate: "2026-04-04T00:00:00Z" },
    { ...expressVpnPerk, Status: "locked", DaysUntilUnlock: 53, UnlockDate: "2026-05-04T00:00:00Z" },
    { ...aloSimPerk, Status: "locked", DaysUntilUnlock: 83, UnlockDate: "2026-06-03T00:00:00Z" },
  ],
  PerksUnlocked: false,
  PerksUnlockDate: "2026-04-04T00:00:00Z",
  PerksTotalValue: 99.98,
};

export const MOCK_SAVINGS_SUMMARY_NO_PERKS: SavingsSummaryResponse = {
  LifetimeTotal: 487.65,
  LifetimePassSavings: 326.5,
  LifetimeCashback: 81.15,
  LifetimeRewardsPerks: 0,
  CurrentPeriodTotal: 45.3,
  CurrentPeriodPass: 45.3,
  CurrentPeriodCashback: 0,
  CurrentPeriodRewardsPerks: 0,
  CurrentPeriodStart: "14 Feb 26",
  CurrentPeriodEnd: "13 Mar 26",
  CurrentTier: "PREMIUM",
  CurrentPlanType: "MONTHLY",
  IsCashbackActive: false,
  TotalCompetitionsParticipated: 12,
  FirstName: "Sarah",
  Surname: "Johnson",
  MemberSince: 1780099200, // 30 May 2026
};

export const MOCK_USER_ADDRESS: UserAddressResponse = {
  HasAddress: true,
  Address: {
    Country: "United Kingdom",
    Postcode: "SW1A 1AA",
    AddressLine1: "10 Downing Street",
    Town: "London",
    County: "Greater London",
  },
};

function buildRewardDetail(reward: Reward): RewardDetailResponse {
  return {
    ...reward,
    WhatsIncluded:
      reward.RewardType === "VOUCHER_CODE"
        ? ["Full subscription included", "No auto-renewal", "Instant activation"]
        : reward.RewardType === "TICKETS"
          ? [`${reward.Quantity ?? 0} Dream Home tickets`, "Entered into next draw", "No action needed"]
          : reward.RewardType === "CREDIT"
            ? ["Automatic cashback on every direct entry", "No minimum spend", "Credits applied instantly"]
            : ["Premium surprise inside", "Exclusive BOTB branded items", "Free delivery"],
    HowToGetIt:
      reward.RewardType === "PHYSICAL"
        ? [
            { StepNumber: 1, Text: "Tap 'Claim Reward' below" },
            { StepNumber: 2, Text: "Confirm or enter your delivery address" },
            { StepNumber: 3, Text: "We'll ship it to you — allow 5-7 working days" },
          ]
        : reward.RewardType === "VOUCHER_CODE"
          ? [
              { StepNumber: 1, Text: "Tap 'Claim Perk' below" },
              { StepNumber: 2, Text: "Copy the voucher code shown" },
              { StepNumber: 3, Text: "Visit the partner website and apply the code at checkout" },
            ]
          : reward.RewardType === "TICKETS"
            ? [
                { StepNumber: 1, Text: "Tap 'Claim Tickets' below" },
                { StepNumber: 2, Text: "Tickets are added to your account instantly" },
                { StepNumber: 3, Text: "You're automatically entered into the next Dream Home draw" },
              ]
            : [
                { StepNumber: 1, Text: "Tap 'Activate Cashback' below" },
                { StepNumber: 2, Text: "Cashback is applied automatically to every direct entry" },
                { StepNumber: 3, Text: "Track your earnings in the savings accordion" },
              ],
  };
}

// ---------------------------------------------------------------------------
// Mock API handlers — each returns { data } after a simulated delay
// ---------------------------------------------------------------------------

export async function mockGetSubscriptionPlan() {
  await delay();
  return { data: MOCK_USER_SUBSCRIPTION };
}

export async function mockGetRewards() {
  await delay();
  return { data: MOCK_REWARDS_LIST_ALL_LOCKED };
}

export async function mockGetRewardDetail(rewardId: string) {
  await delay();
  const allRewards = [...MOCK_REWARDS_LIST_ALL_LOCKED.MemberRewards, ...MOCK_REWARDS_LIST_ALL_LOCKED.MemberPerks];
  const reward = allRewards.find(r => r.Id === rewardId);
  if (!reward) {
    return { error: { status: 404, data: { message: "Reward not found" } } };
  }
  return { data: buildRewardDetail(reward) };
}

export async function mockGetSavingsSummary() {
  await delay();
  return { data: MOCK_SAVINGS_SUMMARY_NO_PERKS };
}

export async function mockGetMarketingSavings() {
  await delay();
  return { data: MOCK_MARKETING_SAVINGS };
}

export async function mockClaimReward(rewardId: string): Promise<{ data: ClaimResponse }> {
  await delay(800);
  const allRewards = [...MOCK_REWARDS_LIST.MemberRewards, ...MOCK_REWARDS_LIST.MemberPerks];
  const reward = allRewards.find(r => r.Id === rewardId);

  if (reward?.RewardType === "VOUCHER_CODE") {
    return {
      data: {
        Success: true,
        Message: "Voucher code revealed",
        ClaimId: "claim-" + rewardId,
        ClaimType: "VOUCHER",
        VoucherCode: "NORTON-7K9X-M2PQ",
        VoucherExpiresAt: "2027-03-04T00:00:00Z",
      },
    };
  }

  if (reward?.RewardType === "CREDIT") {
    return {
      data: {
        Success: true,
        Message: "Cashback activated successfully",
        ClaimId: "claim-" + rewardId,
        ClaimType: "ACTIVATION",
        CashbackPercentage: 2,
      },
    };
  }

  if (reward?.RewardType === "TICKETS") {
    return {
      data: {
        Success: true,
        Message: "Tickets awarded",
        ClaimId: "claim-" + rewardId,
        ClaimType: "TICKETS",
        TicketsAwarded: reward.Quantity ?? 10,
        CompetitionId: "comp-dh-2026-04",
      },
    };
  }

  return {
    data: {
      Success: true,
      Message: "Your reward is on its way!",
      ClaimId: "claim-" + rewardId,
      ClaimType: "PHYSICAL",
      FulfillmentStatus: "PENDING",
      EstimatedDelivery: "5-7 working days",
    },
  };
}

export async function mockGetUserAddress() {
  await delay();
  return { data: MOCK_USER_ADDRESS };
}

export async function mockUpdateUserAddress(): Promise<{ data: UserAddressResponse }> {
  await delay(500);
  return { data: MOCK_USER_ADDRESS };
}
